import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Chip, Stack } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip as RTooltip,
  Legend,
  CartesianGrid,
} from 'recharts';
import { ArrowBackRounded as ArrowBackIcon, RefreshRounded as RefreshIcon } from '@mui/icons-material';

import api from '../../services/api';
import { formatRoleLabel } from '../../utils/roleCapabilities';
import { EmptyState, LoadingState, PageHeader, PageLayout, SectionCard } from '../../components/ui';

// One restrained categorical ramp built from the brand palette. The previous
// dashboard's six-colour set (including the AI-purple #8b5cf6) is gone.
const CHART_COLORS = ['#1f3d7c', '#2389ba', '#1f9d6b', '#c7812f', '#2b7ec8', '#5d6a84'];

const safeArray = (m) => (Array.isArray(m) ? m : []);
const numberOr = (n, fallback = 0) => (Number.isFinite(Number(n)) ? Number(n) : fallback);
const label = (v) => formatRoleLabel(v);

function ChartCard({ title, subtitle, data, height = 220, children, span = 6 }) {
  const isEmpty = !data || (Array.isArray(data) && data.length === 0);
  return (
    <Box sx={{ gridColumn: { xs: 'span 12', lg: `span ${span}` } }}>
      <SectionCard title={title} subheader={subtitle} tone="dashboard" sx={{ height: '100%', borderRadius: '12px' }}>
        {isEmpty ? (
          <EmptyState compact title="No data yet" description="No records in this window." />
        ) : (
          <Box sx={{ height, width: '100%', minWidth: 0 }}>{children}</Box>
        )}
      </SectionCard>
    </Box>
  );
}

export default function AdminAnalytics() {
  const theme = useTheme();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [fetching, setFetching] = useState(true);
  const [error, setError] = useState('');

  async function load() {
    setFetching(true);
    setError('');
    try {
      const res = await api.get('/admin/analytics');
      setData(res.data || {});
    } catch (err) {
      console.error(err);
      setError(err?.response?.data?.message || 'Failed to load analytics.');
      setData(null);
    } finally {
      setFetching(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  const series = useMemo(() => data?.analytics?.series || {}, [data]);
  const breakdowns = useMemo(() => data?.breakdowns || {}, [data]);
  const reportKpis = data?.report_kpis || {};

  const status90d = useMemo(
    () => safeArray(series.vacation_status_90d).map((r) => ({ name: label(r.status || 'unknown'), value: numberOr(r.count) })),
    [series]
  );
  const leaveShare = useMemo(
    () => safeArray(series.leave_share_365d).map((r) => ({ name: label(r.type || 'unknown'), value: numberOr(r.days) })),
    [series]
  );
  const deptLeave = safeArray(series.department_leave_days_12m);
  const headcount12m = safeArray(series.headcount_12m);
  const exitEntry12m = safeArray(series.exit_entry_12m);
  const funnel = safeArray(series.recruiting_funnel);
  const visas = safeArray(series.visa_expiring_30d);
  const byModule = useMemo(
    () => safeArray(breakdowns.by_module).map((r) => ({ module: label(r.module || 'unknown'), count: numberOr(r.count) })),
    [breakdowns]
  );
  const byType = useMemo(
    () =>
      safeArray(breakdowns.by_type)
        .map((r) => ({ type: `${label(r.module || '')} / ${label(r.type || '')}`.trim(), count: numberOr(r.count) }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 12),
    [breakdowns]
  );

  const summaryChips = [
    { label: 'Total', value: numberOr(reportKpis.total_requests) },
    { label: 'Pending', value: numberOr(reportKpis.pending) },
    { label: 'Approved', value: numberOr(reportKpis.approved) },
    { label: 'Rejected', value: numberOr(reportKpis.rejected) },
    { label: 'Completion', value: `${numberOr(reportKpis.completion_rate)}%` },
  ];

  if (fetching) {
    return (
      <PageLayout backgroundAccent compact>
        <LoadingState rows={8} />
      </PageLayout>
    );
  }

  return (
    <PageLayout backgroundAccent compact>
      <PageHeader
        compact
        overline="HR admin"
        title="Workforce & talent analytics"
        subtitle="Periodic reporting. Leave distribution, department load, headcount, movement, hiring and request mix."
        actions={[
          <Button key="back" variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => navigate('/admin/dashboard')}>
            Dashboard
          </Button>,
          <Button key="refresh" variant="outlined" startIcon={<RefreshIcon />} onClick={load}>
            Refresh
          </Button>,
        ]}
      />

      {error ? (
        <EmptyState title="Could not load analytics" description={error} actionLabel="Retry" onAction={load} />
      ) : null}

      <Stack direction="row" spacing={0.8} useFlexGap flexWrap="wrap" sx={{ mb: 1.4 }}>
        {summaryChips.map((c) => (
          <Chip
            key={c.label}
            label={
              <span style={{ fontVariantNumeric: 'tabular-nums' }}>
                {c.label} <b style={{ fontWeight: 600 }}>{c.value}</b>
              </span>
            }
            sx={{ borderRadius: '8px', backgroundColor: alpha(theme.palette.primary.main, 0.07) }}
          />
        ))}
      </Stack>

      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(12, minmax(0, 1fr))', gap: 1.4 }}>
        <ChartCard title="Requests by status" subtitle="Last 90 days" data={status90d} span={4}>
          <ResponsiveContainer>
            <PieChart>
              <Pie data={status90d} dataKey="value" nameKey="name" innerRadius={50} outerRadius={88} paddingAngle={2}>
                {status90d.map((_, i) => (
                  <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                ))}
              </Pie>
              <Legend verticalAlign="bottom" iconType="circle" />
              <RTooltip />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Where leave goes" subtitle="Days by leave type, last 365 days" data={leaveShare} span={4}>
          <ResponsiveContainer>
            <PieChart>
              <Pie data={leaveShare} dataKey="value" nameKey="name" innerRadius={50} outerRadius={88} paddingAngle={2}>
                {leaveShare.map((_, i) => (
                  <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                ))}
              </Pie>
              <Legend verticalAlign="bottom" iconType="circle" />
              <RTooltip />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Requests by module" subtitle="Current window" data={byModule} span={4}>
          <ResponsiveContainer>
            <BarChart data={byModule} layout="vertical" margin={{ top: 4, right: 12, bottom: 4, left: 0 }}>
              <CartesianGrid strokeDasharray="3 6" stroke={alpha(theme.palette.text.secondary, 0.14)} horizontal={false} />
              <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11 }} />
              <YAxis type="category" dataKey="module" tick={{ fontSize: 11 }} width={86} />
              <RTooltip />
              <Bar dataKey="count" fill={theme.palette.secondary.main} radius={[0, 8, 8, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Department leave days" subtitle="Last 12 months, top departments" data={deptLeave} span={6}>
          <ResponsiveContainer>
            <BarChart data={deptLeave} margin={{ top: 6, right: 12, bottom: 4, left: -10 }}>
              <CartesianGrid strokeDasharray="3 6" stroke={alpha(theme.palette.text.secondary, 0.14)} vertical={false} />
              <XAxis dataKey="department" tick={{ fontSize: 11 }} interval={0} angle={-18} textAnchor="end" height={56} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
              <RTooltip />
              <Bar dataKey="days" fill={theme.palette.success.main} radius={[8, 8, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Headcount" subtitle="Running total and new hires, last 12 months" data={headcount12m} span={6}>
          <ResponsiveContainer>
            <LineChart data={headcount12m} margin={{ top: 6, right: 12, bottom: 4, left: -10 }}>
              <CartesianGrid strokeDasharray="3 6" stroke={alpha(theme.palette.text.secondary, 0.14)} />
              <XAxis dataKey="ym" tick={{ fontSize: 11 }} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
              <RTooltip />
              <Legend />
              <Line type="monotone" dataKey="running_total" name="Total" stroke={theme.palette.primary.main} strokeWidth={2.5} dot={false} />
              <Line type="monotone" dataKey="hires" name="New hires" stroke={theme.palette.secondary.main} strokeWidth={2.5} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Exit / entry movement" subtitle="Outcomes by month, last 12 months" data={exitEntry12m} span={6}>
          <ResponsiveContainer>
            <BarChart data={exitEntry12m} margin={{ top: 6, right: 12, bottom: 4, left: -10 }}>
              <CartesianGrid strokeDasharray="3 6" stroke={alpha(theme.palette.text.secondary, 0.14)} vertical={false} />
              <XAxis dataKey="ym" tick={{ fontSize: 11 }} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
              <RTooltip />
              <Legend />
              <Bar dataKey="approved" name="Approved" stackId="s" fill={theme.palette.success.main} radius={[6, 6, 0, 0]} />
              <Bar dataKey="pending" name="Pending" stackId="s" fill={theme.palette.warning.main} />
              <Bar dataKey="rejected" name="Rejected" stackId="s" fill={theme.palette.error.main} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Hiring funnel" subtitle="Candidates by stage" data={funnel} span={6}>
          <ResponsiveContainer>
            <BarChart data={funnel} margin={{ top: 6, right: 12, bottom: 4, left: -10 }}>
              <CartesianGrid strokeDasharray="3 6" stroke={alpha(theme.palette.text.secondary, 0.14)} vertical={false} />
              <XAxis dataKey="stage" tick={{ fontSize: 11 }} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
              <RTooltip />
              <Bar dataKey="count" fill={theme.palette.primary.main} radius={[8, 8, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Request types" subtitle="Top types in the current window" data={byType} span={8}>
          <ResponsiveContainer>
            <BarChart data={byType} margin={{ top: 6, right: 12, bottom: 4, left: -10 }}>
              <CartesianGrid strokeDasharray="3 6" stroke={alpha(theme.palette.text.secondary, 0.14)} vertical={false} />
              <XAxis dataKey="type" tick={{ fontSize: 10 }} interval={0} angle={-20} textAnchor="end" height={70} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
              <RTooltip />
              <Bar dataKey="count" fill={theme.palette.secondary.main} radius={[8, 8, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <Box sx={{ gridColumn: { xs: 'span 12', lg: 'span 4' } }}>
          <SectionCard title="Visas expiring" subheader="Next 30 days, by visa type" tone="dashboard" sx={{ height: '100%', borderRadius: '12px' }}>
            {visas.length ? (
              <Stack direction="row" spacing={0.8} useFlexGap flexWrap="wrap">
                {visas.map((v, i) => (
                  <Chip
                    key={i}
                    label={
                      <span style={{ fontVariantNumeric: 'tabular-nums' }}>
                        {v.visa_type || 'Visa'} <b style={{ fontWeight: 600 }}>{numberOr(v.count)}</b>
                      </span>
                    }
                    sx={{ borderRadius: '8px', backgroundColor: alpha(theme.palette.warning.main, 0.12), color: theme.palette.warning.dark }}
                  />
                ))}
              </Stack>
            ) : (
              <EmptyState compact title="Compliance clear" description="No visas expiring in the next 30 days." />
            )}
          </SectionCard>
        </Box>
      </Box>
    </PageLayout>
  );
}
